// components/DeleteShiftModal.jsx
import React from "react";
import { 
  Modal, 
  Box, 
  Typography, 
  Button, 
  Divider, 
  Stack,
  useTheme,
  useMediaQuery 
} from "@mui/material";

const DeleteShiftModal = ({
  open,
  onClose,
  shiftToDelete,
  deleteShift,
  handleConfirmDelete,
  formatDateTime,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: isMobile ? '90%' : isTablet ? '80%' : 500,
    maxWidth: '100%',
    maxHeight: '90vh',
    bgcolor: "#fff",
    borderRadius: 4,
    boxShadow: "0 8px 30px rgba(0,0,0,0.1)",
    p: isMobile ? 2 : 4,
    overflowY: 'auto',
  };

  const buttonStyle = {
    minWidth: isMobile ? 'auto' : 120,
    padding: isMobile ? '6px 12px' : '8px 16px',
    fontSize: isMobile ? '0.8125rem' : '0.875rem',
  };

  return (
    <Modal 
      open={open} 
      onClose={onClose}
      disableScrollLock={false}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(2px)',
        },
      }}
    >
      <Box sx={modalStyle}>
        <Typography 
          variant="h6" 
          color="error" 
          fontWeight="bold" 
          gutterBottom
          fontSize={isMobile ? '1.125rem' : '1.25rem'}
        >
          Confirm Deletion
        </Typography>

        <Typography 
          variant="body1" 
          mb={2}
          fontSize={isMobile ? '0.9375rem' : '1rem'}
        >
          Are you sure you want to delete this shift? This action cannot be
          undone.
        </Typography>

        {shiftToDelete && (
          <Box
            sx={{
              bgcolor: "#f9f9f9",
              borderRadius: 2,
              p: isMobile ? 1.5 : 2,
              mb: 3,
              border: "1px solid #e0e0e0",
            }}
          >
            <Typography 
              variant="body2" 
              mb={1}
              fontSize={isMobile ? '0.875rem' : '0.9375rem'}
            >
              <strong>Branch:</strong> {shiftToDelete.hospitalName}
            </Typography>
            <Typography 
              variant="body2" 
              mb={1}
              fontSize={isMobile ? '0.875rem' : '0.9375rem'}
            >
              <strong>Date:</strong>{" "}
              {formatDateTime(shiftToDelete.startDate, shiftToDelete.startTime)}
            </Typography>
            <Typography 
              variant="body2"
              fontSize={isMobile ? '0.875rem' : '0.9375rem'}
            >
              <strong>Duration:</strong> {shiftToDelete.duration} hours
            </Typography>
          </Box>
        )}

        <Divider sx={{ mb: 3 }} />

        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          justifyContent="flex-end" 
          spacing={2}
          sx={{
            '& > button': {
              width: isMobile ? '100%' : 'auto',
            },
            '& > button + button': {
              ml: { xs: 0, sm: 2 },
              mt: { xs: 2, sm: 0 },
            }
          }}
        >
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              ...buttonStyle,
              borderColor: "#6c757d",
              color: "#6c757d",
              "&:hover": {
                borderColor: "#5a6268",
                backgroundColor: "#f8f9fa",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={deleteShift.isPending}
            sx={buttonStyle}
          >
            {deleteShift.isPending ? "Deleting..." : "Delete Shift"}
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
};

export default DeleteShiftModal;
